import React, { useContext, useEffect, useState } from 'react'
import { useSnackbar } from 'react-simple-snackbar'
import { CandidateContext } from '../../../../context/CandidateContext';
import { Form } from 'react-bootstrap';
import '../../../../assets/css/knowing_ifa.css'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { API } from '../../../../http-common';
import { API_URL } from '../../../../constants';
import { formatDateToInput } from '../../../../helpers/formatDate';
import { ADVANCE_STATUS } from '../../../../helpers/constants';

export const PsychoTech = ({ readOnly = false }) => {

    const { candidateData, setCandidateData } = useContext(CandidateContext)
    const checkContractStatus = candidateData.prospect_status === 100;

    const initialState = {
        evaluation_date: '',
        notes: '',
        evaluation_status: 0,
        advance_status: ''
    }

    const errorInitialState = {
        evaluation_date: '',
        evaluation_status: '',
        notes: '',
        advance_status: ''
    }

    const [psychotechnical, setPsychotechnical] = useState(initialState)
    const [errors, setErrors] = useState(errorInitialState)
    const [working, setWorking] = useState(false)
    const today = new Date().toISOString().split('T')[0];
    const isDisabled = working || checkContractStatus || readOnly;

    const [openSnackbarSuccess] = useSnackbar({
        position: 'bottom-right',
        style: { backgroundColor: 'var(--bs-green)', color: '#fff', fontSize: '20px', marginBottom: '5rem' }
    })

    const [openSnackbarError] = useSnackbar({
        position: 'bottom-right',
        style: { backgroundColor: 'var(--bs-red)', color: '#fff', fontSize: '20px', marginBottom: '5rem' }
    })

    useEffect(() => {
        if (candidateData.id) {
            getPsychotechnical()
        }
    }, [candidateData.id])

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target

        const newValue = type === 'checkbox' ? (checked ? 1 : 0) : value

        setPsychotechnical(prev => ({
            ...prev,
            [name]: newValue
        }))

        setErrors(prev => ({
            ...prev,
            [name]: ''
        }))

        if (name === 'evaluation_date' && value > today) {
            setErrors(prev => ({
                ...prev,
                evaluation_date: 'No puedes asignar una fecha posterior al día de hoy.'
            }))
            openSnackbarError('Fecha inválida')
        }
    }

    const handleAdvanceStatusChange = (e) => {
        const { value, checked } = e.target
        const numericValue = Number(value)

        setPsychotechnical(prev => ({
            ...prev,
            advance_status: checked ? numericValue : ''
        }))

        setErrors(prev => ({
            ...prev,
            advance_status: ''
        }))
    }

    const checkInputs = () => {
        let success = true
        const newErrors = { ...errorInitialState }

        if (!psychotechnical.evaluation_date) {
            newErrors.evaluation_date = 'El campo es requerido'
            success = false
        }

        if (!psychotechnical.evaluation_status) {
            newErrors.evaluation_status = 'Debe marcar si el psicotécnico fue realizado'
            success = false
        }

        if (!psychotechnical.advance_status) {
            newErrors.advance_status = 'Debe marcar Avanza, Avanza con Reservas o No Avanza'
            success = false
        }

        if (!psychotechnical.notes || psychotechnical.notes.trim() === '') {
            newErrors.notes = 'Ingrese las notas correspondientes'
            success = false
        }

        if (psychotechnical.evaluation_date && psychotechnical.evaluation_date > today) {
            newErrors.evaluation_date = 'No puedes asignar una fecha posterior al día de hoy.'
            success = false
        }

        setErrors(newErrors)

        if (!success) {
            openSnackbarError('Campos requeridos')
        }

        return success
    }

    const handleSubmit = (e) => {
        e.preventDefault()
        const check = checkInputs()

        if (check) {
            savePsychotechnical();
        }
    }

    const getPsychotechnical = async () => {
        try {
            const response = await API.get(`/prospects/psychotechnical/${candidateData.id}`)

            const data = response.data.psychotechnical_data

            if (data) {
                setPsychotechnical({
                    evaluation_date: formatDateToInput(data.evaluation_date),
                    evaluation_status: data.evaluation_status || 0,
                    advance_status: data.advance_status || '',
                    notes: data.notes || '',
                })
            } else {
                setPsychotechnical(initialState)
            }

        } catch (err) {
            console.log('getPsychotechnical error =>', err)
            openSnackbarError('No se pudieron cargar los datos del psicotécnico')
        }
    }

    const savePsychotechnical = async () => {
        const payload = {
            evaluation_date: psychotechnical.evaluation_date,
            evaluation_status: psychotechnical.evaluation_status,
            notes: psychotechnical.notes,
            advance_status: psychotechnical.advance_status
        }

        try {
            setWorking(true)

            await API.post(`/prospects/psychotechnical/${candidateData.id}`, payload)
            openSnackbarSuccess('¡Datos guardados!')
        } catch (err) {
            console.log('savePsychotechnical error =>', err.response || err)
            openSnackbarError('No se pudo guardar el psicotécnico')
        } finally {
            setWorking(false)
        }
    }

    const makeErrorField = (text) => {
        return (
            <Form.Text
                className="text-danger fw-bold"
                style={{ fontSize: "11px", lineHeight: "0" }}
            >
                {text}
            </Form.Text>
        )
    }

    const refreshCandidateData = async () => {
        try {
            const res = await API.get(`prospects/${candidateData.id}`);
            setCandidateData(res.data.data || res.data);
        } catch (error) {
            console.log("Error refrescando candidato: ", error);
            openSnackbarError("No se pudo actualizar la información del candidato");
        }
    };

    const uploadPsychotechnicalFile = async (e) => {
        try {
            const file = e.target.files?.[0];

            if (!file) return;

            const dataForm = new FormData();
            dataForm.append("psychotechnical", file);

            await API.post(
                `/prospects/uploads/psychotechnical/doc_psychotechnical/${candidateData.id}`,
                dataForm,
                { timeout: 10000 }
            );

            await refreshCandidateData();

            openSnackbarSuccess("Documento psicotécnico cargado correctamente");
            e.target.value = "";
        } catch (err) {
            console.log(err);
            openSnackbarError("Error al cargar documento psicotécnico");
        }
    };

    const deletePsychotechnicalFile = async () => {
        try {
            await API.post(
                `prospects/uploads/delete/${candidateData.doc_psychotechnical}/${candidateData.id}`
            );

            await refreshCandidateData();

            openSnackbarSuccess("Documento psicotécnico eliminado correctamente");
        } catch (err) {
            console.log(err);
            openSnackbarError("Error al eliminar documento psicotécnico");
        }
    };

return (

        <div className="col-md-12 item my-4 px-0" id="container_knowing_my_ifa">

            <div className="row px-2 h3 text-uppercase my-5 border-bottom">Psicotécnico</div>
            <form action="#" id="form_knowing_my_ifa" onSubmit={ (e) => handleSubmit(e) }>
                <>
                    <div className="form-row d-flex align-items-baseline">
                        <div className="col-md-6 d-flex">
                        <label className="data_label blue_title me-2">Fecha de realización</label>

                        <input
                            onChange={handleChange}
                            type="date"
                            value={psychotechnical.evaluation_date || ''}
                            placeholder="Fecha de realización"
                            name="evaluation_date"
                            id="evaluation_date"
                            max={today}
                            className="data_item blue_title me-2"
                            disabled={isDisabled}
                        />

                        {errors.evaluation_date && makeErrorField(errors.evaluation_date)}
                        </div>

                        <div className="col-md-6">
                            <label id="canal_redes" className="form-check-inline data_label blue_title">
                                <input
                                onChange={handleChange}
                                disabled={isDisabled}
                                checked={Number(psychotechnical.evaluation_status) === 1}
                                type="checkbox"
                                className="form-check-input"
                                name="evaluation_status"
                                id="evaluation_status"
                            />
                            <span className="ms-2">Realizado psicotécnico</span>

                            {errors.evaluation_status && makeErrorField(errors.evaluation_status)}
                            </label>
                        </div>
                    </div>
                    <br/>
                </>
                

                <br/><br/>

                    <div className="form-row">
                        <div className="col-md-6">
                            <span className="data_label blue_title d-block">
                                Notas de la evaluación
                                {errors.notes &&
                                    <span
                                        className="text-danger ms-2 fw-bold"
                                        style={{ fontSize: '12px' }}
                                    >
                                        {errors.notes}
                                    </span>
                                }
                            </span>

                            <textarea
                                onChange={handleChange}
                                value={psychotechnical.notes || ''}
                                name="notes"
                                id="notes"
                                className="form-control data_item"
                                cols="30"
                                rows="7"
                                disabled={isDisabled}
                            />

                        </div>
                    </div>

                    <div className="col-md-6 d-flex flex-column mt-4">
                        <label className="form-check-inline data_label blue_title">
                            <input
                                onChange={handleAdvanceStatusChange}
                                disabled={isDisabled}
                                checked={psychotechnical.advance_status === ADVANCE_STATUS.AVANZA}
                                type="checkbox"
                                className="form-check-input"
                                name="advance_status"
                                id="advance_status_avanza"
                                value={ADVANCE_STATUS.AVANZA}
                            />
                            <span className="ms-2">Avanza</span>
                        </label>

                        <label className="form-check-inline data_label blue_title mt-1">
                            <input
                                onChange={handleAdvanceStatusChange}
                                disabled={isDisabled}
                                checked={psychotechnical.advance_status === ADVANCE_STATUS.AVANZA_RESERVAS}
                                type="checkbox"
                                className="form-check-input"
                                name="advance_status"
                                id="advance_status_avanza_reservas"
                                value={ADVANCE_STATUS.AVANZA_RESERVAS}
                            />
                            <span className="ms-2">Avanza con Reservas</span>
                        </label>

                        <label className="form-check-inline data_label blue_title mt-1">
                            <input
                                onChange={handleAdvanceStatusChange}
                                disabled={isDisabled}
                                checked={psychotechnical.advance_status === ADVANCE_STATUS.NO_AVANZA}
                                type="checkbox"
                                className="form-check-input"
                                name="advance_status"
                                id="advance_status_no_avanza"
                                value={ADVANCE_STATUS.NO_AVANZA}
                            />
                            <span className="ms-2">No Avanza</span>
                        </label>

                        {errors.advance_status && makeErrorField(errors.advance_status)}
                    </div>

                    <div className="mt-4">
                        {candidateData.doc_psychotechnical === 0 ? (
                            <div>
                                <input
                                    type="file"
                                    id="document_psychotechnical"
                                    name="psychotechnical"
                                    onChange={uploadPsychotechnicalFile}
                                    hidden
                                />

                                <button
                                    type="button"
                                    onClick={() => document.getElementById("document_psychotechnical").click()}
                                    className="btn blue_btn balanz_btn_longx"
                                    disabled={isDisabled}
                                >
                                    CARGAR DOCUMENTO PSICOTÉCNICO
                                </button>
                            </div>
                        ) : (
                            <div>
                                <a href={API_URL+`/prospects/${candidateData.id}/file/${candidateData.doc_psychotechnical}`} target="_blank" className="btn gray_btn button_cv balanz_btn_longx">VER PSICOTÉCNICO</a>
                                <FontAwesomeIcon
                                    style={{ marginLeft: "10px", color: "red", cursor: "pointer" }}
                                    icon={faTrash}
                                    onClick={() => deletePsychotechnicalFile()}
                                />
                            </div>
                        )}
                    </div>

                <br/>
                

                <br/>
                <div className="row">
                    <div className="d-flex justify-content-end align-items-end">
                        <button
                        type="submit"
                        name="form_psychotechnical"
                        id="form_psychotechnical"
                        className="btn blue_btn balanz_btn_longx"
                        disabled={isDisabled}
                    >
                        Guardar
                    </button>
                    </div>
                </div>
            </form>
        </div>
    )
}